import googleLibPhoneNumber from 'google-libphonenumber';
import * as yup from 'yup';

import { getDialCodeById, validateEmail } from './formHelpers';
import { t } from './localization';

function loadPhoneNumberUtil() {
  return googleLibPhoneNumber;
}

export const urlWithoutProtocolRegex =
  // eslint-disable-next-line no-useless-escape
  /^(((https?|ftp):)?\/\/)?(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(\#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|\/|\?)*)?$/i;

export const isValidEmailFormat = async (email) => {
  const isEmailValid = await validateEmail(email);
  return isEmailValid;
};

export const isValidPhoneFormat = (value) => {
  const { PhoneNumberUtil } = loadPhoneNumberUtil();
  try {
    const phoneUtil = PhoneNumberUtil.getInstance();
    const number = phoneUtil.parseAndKeepRawInput(value);
    return !!phoneUtil.isValidNumber(number);
  } catch (e) {
    return false;
  }
};

export const validateOptionalPhoneNumberWithDialCode = (
  value,
  dialCodeId
) => {
  // Null is important to be thruthy for the email to allow for the not required property
  if (!value || value.length === 0) {
    return true;
  }

  if (value.includes('+')) {
    return true;
  }

  if (!dialCodeId) {
    return true;
  }

  if (dialCodeId) {
    const dialCode = getDialCodeById(dialCodeId);
    const fullPhoneNumber = `${dialCode}${value}`;
    return isValidPhoneFormat(fullPhoneNumber);
  }
  return false;
};

export const validate = async ({ schema, formData, setErrors }) => {
  setErrors({});

  let isValid = await schema
    .validate(formData, {
      abortEarly: false
    })
    .then(() => true)
    .catch((validationErr) => {
      if (Array.isArray(validationErr.inner)) {
        let errors = {};
        validationErr.inner.forEach(
          ({ path, message }) => (errors[path] = message)
        );
        setErrors(errors);
      }
      return false;
    });

  return isValid;
};

// TODO : Maybe move this to a utils file
// https://stackoverflow.com/questions/56725383/create-dynamic-yup-validation-schema-from-json
export const createYupSchema = (schema, config) => {
  const {
    label,
    inputSectionKey,
    fieldName,
    isRequired = false,
    isDeleted,
    isSunlightUrl
  } = config;

  //if the field is deleted from schema then do not include it in validation
  if (isDeleted) return schema;

  let validator = null;
  switch (inputSectionKey) {
    case 'text':
      validator = yup.string();
      if (isSunlightUrl) {
        validator = validator.matches(
          urlWithoutProtocolRegex,
          t('label-should-be-a-valid-url', {
            label: label
          })
        );
      }
      if (isRequired) {
        validator = validator.required(
          t('label-is-required-0', { label: label })
        );
      }
      break;
    case 'phone':
      validator = yup.string();
      if (isRequired) {
        validator = validator.required(
          t('label-is-required-0', { label: label })
        );
      }
      validator = validator.test(
        'phoneNumberValidation',
        t('invalid-phone-number'),
        (value) => isValidPhoneFormat(value)
      );
      break;
    case 'email':
      validator = yup.string().email(
        t('label-must-be-a-valid-email', {
          label: label
        })
      );
      if (isRequired) {
        validator = validator.required(
          t('label-is-required-0', { label: label })
        );
      }
      break;
    case 'url':
      validator = yup.string().url(
        t('label-should-be-a-valid-url', {
          label: label
        })
      );
      if (isRequired) {
        validator = validator.required(
          t('label-is-required-0', { label: label })
        );
      }
      break;
    case 'number':
      validator = yup.number(
        t('label-must-be-a-number', { label: label })
      );
      if (isRequired) {
        validator = validator.required(
          t('label-is-required-0', { label: label })
        );
      }
      break;
    case 'date':
      validator = yup.date(
        t('label-should-be-a-valid-date', { label: label })
      );
      if (isRequired) {
        validator = validator.required(
          t('label-is-required-0', { label: label })
        );
      }
      break;
    case 'checkbox':
      validator = yup.object();
      if (isRequired) {
        validator = validator.required(
          t('label-is-required-0', { label: label })
        );
        validator = validator.test(
          'groupCheckboxValidation',
          t('please-select-at-least-1'),
          (value) => {
            return !!Object.values(value).find(Boolean);
          }
        );
      }
      break;
    case 'radio':
      validator = yup.string();
      if (isRequired) {
        validator = validator.required(
          t('label-is-required-0', { label: label })
        );
      }
      break;
    case 'multi-select-modal':
      validator = yup.object();
      if (isRequired) {
        validator = validator.test(
          'groupCheckboxValidation',
          t('please-select-at-least-1'),
          (value) => {
            return !!Object.values(value).find(Boolean);
          }
        );
      }
      break;
    default:
      // do nothing
      break;
  }

  if (!validator) {
    return schema;
  }

  schema[fieldName] = validator;
  return schema;
};
